import { useEffect, useRef, useState } from 'react';

import { LuSearch, LuSparkles } from 'react-icons/lu';

import { googleAiModeUrl, googleSearchUrl } from '../headerSearchUrls';

// The key left of "1" on a Finnish keyboard.
const FOCUS_SHORTCUT = '§';

/** Google search field for the dashboard header. Desktop-only; never autofocuses. */
export default function HeaderSearch() {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== FOCUS_SHORTCUT || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target) || document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const url = googleSearchUrl(query);
    if (url) window.location.assign(url);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (query) setQuery('');
      else inputRef.current?.blur();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      const url = googleSearchUrl(query);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <form
      role="search"
      onSubmit={onSubmit}
      className="flex h-11 w-full max-w-120 min-w-0 items-center gap-3 rounded-full border border-(--border-subtle) bg-(--bg-surface-strong) pr-1.5 pl-4 shadow-(--shadow-widget) transition-[box-shadow,border-color] focus-within:border-(--accent-primary) focus-within:shadow-[0_4px_16px_-4px_rgba(0,0,0,0.25)] focus-within:ring-2 focus-within:ring-(--accent-primary)/30 hover:border-(--border-default) hover:shadow-[0_4px_16px_-4px_rgba(0,0,0,0.25)]"
    >
      <LuSearch aria-hidden className="h-5 w-5 shrink-0 text-(--text-tertiary)" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search Google"
        aria-label="Search Google"
        title={`Search Google (${FOCUS_SHORTCUT})`}
        aria-keyshortcuts={FOCUS_SHORTCUT}
        autoComplete="off"
        enterKeyHint="search"
        className="h-full min-w-0 flex-1 bg-transparent text-base text-(--text-primary) outline-none placeholder:text-(--text-tertiary)"
      />
      <a
        href={googleAiModeUrl(query)}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-(--bg-surface-hover) px-3 text-sm font-medium text-(--text-secondary) transition-colors hover:text-(--text-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-primary)"
      >
        <LuSparkles aria-hidden className="h-4 w-4" />
        AI Mode
      </a>
    </form>
  );
}

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement && (target.isContentEditable || target.matches('input, textarea, select'))
  );
}
